'use client';

/**
 * NarrativeHandoff — the boundary micro-bridges + the priced-boundary
 * DisclaimerDivider slot that interleave the landing chapters (Judge F verdict
 * Decisions 2-4, §2.1 funnel map; R3.5 remediation brief §2 / mechanics spec
 * §2 "BRIDGE SIMPLIFICATION"). Converged onto the SAME clean grammar as
 * `DisclaimerDivider` below: two solid half-band layers meeting at ONE
 * midpoint hairline seam — never a gradient hand-off, never a second trace —
 * with a compact status tile (opt-in per boundary via `showChip`) centered on
 * the seam between two short (24px) static dashed leaders.
 *
 * R3.5 fix (brief §2 "absolutely horrendous" verdict): the previous alternating
 * SVG circuit trace read as a third strip of color between two flat neighbor
 * bands, and "LEAD CAPTURED" floated as an orphan pill. Both are deleted here
 * — the seam is the only visible line, and the chip is curated per boundary
 * ("fewer, better") instead of appearing on every bridge.
 *
 * Motion contract (reduced from Judge F Dec 3 — trace removed, chip kept):
 *  - The chip's INNER child fades/rises (opacity + translateY(8→0), `enter`
 *    420ms) once the boundary enters view. One-shot.
 *  - Raw WAAPI with fill:'forwards' commits (commitStyles) then cancel()s.
 *  - Runs on desktop (>=1024px) + no-reduced-motion + visible tab ONLY. Mobile,
 *    reduced motion and no-JS all render the fully-drawn static end state
 *    (chip at rest; leaders are always static — never animated).
 *  - `showChip={false}` boundaries render a single centered static leader
 *    through the seam and skip the entrance effect entirely (nothing to
 *    animate — no chip node exists to reveal).
 *  - The visual group is aria-hidden; the wrapper carries ONE sr-only bilingual
 *    funnel sentence so the narrative survives for assistive tech (Dec 4).
 *
 * All durations/easings come from `landing-motion.ts` tokens — no literals at a
 * call site (`check-landing-choreography`). All copy is passed in from
 * `content.ts` locale objects — no inline strings here (Judge F file plan).
 *
 * 2026-07-18 slop-audit revision pass FIX 2 (HIGH): this file was
 * deliberately left untouched by the R7 hallmark paper flip (its own
 * `fromBand`/`toBand` props are just hex strings the CALLER supplies —
 * nothing here hardcoded a dark band) — that doctrine is AMENDED now that
 * a live census found the real bug it created: `isLightSeam`'s fallback
 * sentinel (`LIGHT_PAPER_HEX = '#F6F5F2'`, the OLD `ui-light/light-tokens.
 * ts` paper hex) never matched any `ui/paper-tokens.ts` `PAPER_BAND` hex,
 * so a caller that omits `surface` — `LandingPage.tsx`'s Bot→FAQ and
 * Trust→FinalCTA bridges, both paper-to-paper post-R7 — fell through to
 * `isLightSeam=false`, rendering the DARK_SURFACE-filled floating chip
 * directly on a paper page (banned page chrome outside exhibit frames).
 * The fallback now ALSO recognizes the `PAPER_BAND` family (base/alt/deep
 * — NOT `mast`, the one genuinely-dark ink band, which no bridge on this
 * page transitions into), so any FUTURE caller that forgets `surface` on
 * a paper-to-paper boundary is safe by default — the class is fixed, not
 * just today's two call sites. Practical effect on the real page: every
 * bridge is paper-to-paper today, so all 5 now render the quiet,
 * leaderless/chipless 32px grammar (`icon`/`label` become inert on the 2
 * that used to show a chip — their content survives only via the sr-only
 * `srText` sentence, exactly like the two seams that already worked this
 * way). `DisclaimerDivider`'s kicker + leaders + midpoint seam (its OWN
 * always-paper boundary, Pricing→Bot) are re-inked to `PAPER_INK`/paper-
 * safe stroke colors in the same pass — see its own doc comment below.
 */

import { useEffect, useRef, type CSSProperties } from 'react';
import { inView } from 'motion';
import { Inbox, Workflow, ChartNoAxesCombined, Bot, CheckCheck } from 'lucide-react';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import type { BilingualContent } from '@/features/marketing/hooks/useLocale';
import { useMediaQuery, useReducedMotion } from '@/features/marketing/hooks/useMediaQuery';
import MarketingDisclaimer, { type MarketingDisclaimerCopy } from './MarketingDisclaimer';
import {
  LANDING_MOTION_MS,
  LANDING_EASE_CSS,
  LANDING_INVIEW,
} from '@/features/marketing/motion/landing-motion';
import { LIGHT_TOKENS } from '@/features/marketing/ui-light/light-tokens';
import { assertFilled } from '@/features/marketing/ui/dev-invariants';
import { DARK_INK, DARK_SURFACE } from '@/features/marketing/ui/dark-tokens';
import { PAPER_BAND, PAPER_INK } from '@/features/marketing/ui/paper-tokens';

/**
 * 2026-07-18 slop-audit revision pass FIX 2: the paper-family hex set
 * `isLightSeam`'s fallback recognizes, alongside the pre-existing
 * `LIGHT_PAPER_HEX` sentinel. Deliberately `base`/`alt`/`deep` — the three
 * genuinely light-luminance paper steps. `mast` (`PAPER_BAND.mast`, the
 * one dark ink block, the footer close) is NOT a member of this set, but
 * note the fallback is an EITHER-side OR (mirrors the pre-existing
 * `LIGHT_PAPER_HEX` check's own "either side" semantics, not a new rule):
 * a hypothetical `deep`↔`mast` boundary would still resolve `isLightSeam`
 * TRUE via the `deep` side alone, matching the established "a boundary
 * touching light reads as quiet" principle every other light seam on this
 * page already uses (no real bridge on the live page touches `mast` at
 * all — Final→Footer has no bridge — so this is a documented edge case,
 * not a live behavior).
 */
const PAPER_FAMILY_HEXES: ReadonlySet<string> = new Set([
  PAPER_BAND.base.hex,
  PAPER_BAND.alt.hex,
  PAPER_BAND.deep.hex,
]);

/** The chapter-band luminance ramp is owned by `ui/dark-tokens.ts`'s
 * `DARK_BAND`/`DARK_FIGURE` registry (R6 warm retint, pre-R7) and, since
 * R7's hallmark paper flip, `ui/paper-tokens.ts`'s `PAPER_BAND` registry
 * (every real bridge on the live page today) — plus the legacy R4-P3
 * `#F6F5F2` sentinel (`[AMENDED per B1/B4]`, superseded by `PAPER_BAND` but
 * kept for back-compat, see `PAPER_FAMILY_HEXES` above). Deliberately a
 * plain `string`, not a closed literal union of the band hexes: each
 * registry is the single source of truth for which hexes are legal within
 * it (R6 integration fix) — re-declaring that same enumeration here as a
 * literal union would be exactly the "second, driftable copy" both
 * registries' own file headers warn against; every real call site
 * (LandingPage.tsx) sources its value from `PAPER_BAND.*.hex` today. */
type LandingBand = string;

type BridgeDirection = 'left' | 'right';

/**
 * Icons resolved by string key rather than a passed component reference: the
 * interleaving parent (LandingPage) is a server component, and a Client-
 * Component boundary cannot receive a function/component prop. Deviation from
 * Judge F's "icon component" prop, forced by RSC serialization; the mapping is
 * one-to-one with §2.1 (Inbox/Workflow/ChartNoAxesCombined/Bot/CheckCheck).
 */
type BridgeIconKey = 'inbox' | 'workflow' | 'chart' | 'bot' | 'check';

const BRIDGE_ICONS = {
  inbox: Inbox,
  workflow: Workflow,
  chart: ChartNoAxesCombined,
  bot: Bot,
  check: CheckCheck,
} as const;

export interface NarrativeHandoffProps {
  fromBand: LandingBand;
  toBand: LandingBand;
  direction: BridgeDirection;
  /** Unused when `surface==='light'` (no chip renders) — optional so a
   * light-seam call site doesn't have to invent a placeholder. */
  label?: BilingualContent;
  srText: BilingualContent;
  /** Unused when `surface==='light'`. */
  icon?: BridgeIconKey;
  /** Trust→Final only: warm the seam hairline + chip border toward the amber pole, taller root. */
  warmToFinal?: boolean;
  /**
   * Per-boundary curation ("fewer, better" — brief §2.2): most bridges keep
   * the status tile, but a bridge that doesn't earn a waypoint marker (e.g.
   * hero→product, where "LEAD CAPTURED" floated as an orphan pill) drops it
   * and renders a single centered leader through the seam instead. Ignored
   * when `surface==='light'` (B4 forces chipless regardless).
   */
  showChip?: boolean;
  /**
   * R4-P3 masterplan §1.3, [AMENDED per B4] — a "light" boundary (any
   * transition touching the paper/light family) is leaderless AND
   * chipless: a 32px-max root, two neighbor-color halves meeting at ONE
   * neutral 1px midpoint hairline, no shadow, no dashed leaders, no label
   * — "matching the reference's direct dark→white editorial cut." When
   * omitted, defaults from `fromBand`/`toBand`: the legacy `#F6F5F2`
   * sentinel OR (2026-07-18 slop-audit revision pass FIX 2) membership in
   * `ui/paper-tokens.ts`'s `PAPER_BAND` family (base/alt/deep) — so a
   * caller that forgets the prop on a paper-to-paper boundary still gets
   * the correct quiet treatment, not the dark floating chip.
   */
  surface?: 'dark' | 'light';
}

export default function NarrativeHandoff({
  fromBand,
  toBand,
  label,
  srText,
  icon,
  warmToFinal = false,
  showChip = true,
  surface,
}: NarrativeHandoffProps) {
  const { t } = useLocale();
  const reduced = useReducedMotion();
  const isDesktop = useMediaQuery('(min-width: 1024px)', false);
  const shouldAnimate = isDesktop && !reduced;
  const LIGHT_PAPER_HEX: LandingBand = '#F6F5F2';
  const isLightSeam = surface
    ? surface === 'light'
    : fromBand === LIGHT_PAPER_HEX ||
      toBand === LIGHT_PAPER_HEX ||
      PAPER_FAMILY_HEXES.has(fromBand) ||
      PAPER_FAMILY_HEXES.has(toBand);

  const rootRef = useRef<HTMLDivElement>(null);
  const chipInnerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const chipInner = chipInnerRef.current;
    const rootEl = rootRef.current;
    // showChip===false → chipInner is never mounted → nothing to animate,
    // static leader-only composition (brief §2.2 "fewer, better").
    if (!chipInner || !rootEl) return;

    // Static composition (mobile / reduced motion / SSR default): chip at
    // rest. Matches the server markup — no rewind.
    if (!shouldAnimate) {
      chipInner.style.opacity = '1';
      chipInner.style.transform = 'none';
      return;
    }

    // Desktop + motion: arm the one-shot rise. Hide until the boundary enters.
    chipInner.style.opacity = '0';
    chipInner.style.transform = 'translateY(8px)';

    const running: Animation[] = [];
    let fired = false;

    const commitAndRelease = (anim: Animation) => {
      anim.finished
        .then(() => {
          // WAAPI fill:'forwards' hygiene — bake the end state, drop the handle.
          anim.commitStyles();
          anim.cancel();
        })
        .catch(() => {
          /* cancelled on unmount before finish — nothing to commit */
        });
    };

    const stop = inView(
      rootEl,
      () => {
        if (fired) return;
        fired = true;

        // Hidden tab: never animate off-screen — commit the final state instead.
        if (typeof document !== 'undefined' && document.hidden) {
          chipInner.style.opacity = '1';
          chipInner.style.transform = 'none';
          return;
        }

        const chip = chipInner.animate(
          [
            { opacity: 0, transform: 'translateY(8px)' },
            { opacity: 1, transform: 'none' },
          ],
          {
            duration: LANDING_MOTION_MS.enter,
            delay: LANDING_MOTION_MS.enter,
            easing: LANDING_EASE_CSS.enter,
            fill: 'forwards',
          }
        );
        running.push(chip);
        running.forEach(commitAndRelease);
      },
      { amount: LANDING_INVIEW.connector.amount, margin: LANDING_INVIEW.connector.margin }
    );

    return () => {
      stop();
      running.forEach((anim) => {
        try {
          anim.cancel();
        } catch {
          /* already released via commitStyles path */
        }
      });
    };
  }, [shouldAnimate]);

  // [AMENDED per B4] a dark↔light boundary is leaderless AND chipless
  // regardless of the `showChip` prop — 32px root cap, no `warmToFinal`
  // scale (light seams never warm toward amber; Law #4 is ink-only).
  const chipVisible = !isLightSeam && showChip;
  const Icon = chipVisible && icon ? BRIDGE_ICONS[icon] : null;
  if (chipVisible && (!icon || !label)) {
    assertFilled(
      false,
      'NarrativeHandoff: a chip-visible dark seam requires both `icon` and `label`.'
    );
  }
  // Trust→Final is the only warm + taller bridge (96/56); neutral bridges
  // are 80/48; a dark↔light seam is a flat 32px cap at every breakpoint.
  const heightClass = isLightSeam
    ? 'h-8'
    : warmToFinal
      ? 'h-[56px] lg:h-[96px]'
      : 'h-[48px] lg:h-[80px]';
  const seamBackground = isLightSeam
    ? `var(--connector-seam-light, ${LIGHT_TOKENS.seam.hairline})`
    : warmToFinal
      ? 'var(--connector-warm, #f59e0b66)'
      : 'var(--connector-seam, #FFFFFF12)';

  return (
    <div
      ref={rootRef}
      data-narrative-bridge
      data-surface={isLightSeam ? 'light' : 'dark'}
      className={`landing-motion-scope relative w-full ${heightClass}`}
    >
      <span className="sr-only">{t(srText)}</span>

      <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
        {/* Two solid half-band layers (never a gradient hand-off). These EQUAL
            the neighbor section bands, so the top edge is seamless into
            section A and the bottom edge seamless into section B — the ONLY
            visible line is the seam below. */}
        <div className="absolute inset-x-0 top-0 h-1/2" style={{ background: fromBand }} />
        <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: toBand }} />
        {/* One midpoint hairline seam. Trust→Final warms toward the amber
            pole it abuts (brief §2.4) — a whisper, the pole owns the amber.
            A dark↔light seam ([AMENDED per B4]) uses the neutral light
            hairline instead — no shadow, no dashed marks, no label. */}
        <div
          className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2"
          style={{ background: seamBackground }}
        />
      </div>

      {isLightSeam ? null : chipVisible && Icon ? (
        <>
          {/* 24px static dashed leaders above + below the chip (identical
              recipe to DisclaimerDivider below — one dash vocabulary). */}
          <div
            aria-hidden="true"
            className="narrative-leader absolute left-1/2 top-0 h-6 w-px -translate-x-1/2"
          />
          <div
            aria-hidden="true"
            className="narrative-leader absolute bottom-0 left-1/2 h-6 w-px -translate-x-1/2"
          />

          {/* Floating status tile — OUTER wrapper centers, INNER child animates. */}
          <div
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <span
              ref={chipInnerRef}
              className="narrative-chip-inner flex items-center will-change-[opacity,transform]"
              style={{
                minHeight: 32,
                paddingLeft: 10,
                paddingRight: 10,
                gap: 6,
                borderRadius: 8,
                background: DARK_SURFACE.base.hex,
                border: warmToFinal ? '1px solid #F59E0B3D' : '1px solid #FFFFFF1A',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,.04), 0 12px 32px rgba(0,0,0,.28)',
              }}
            >
              <Icon
                width={14}
                height={14}
                strokeWidth={2}
                color={DARK_INK.tertiary.hex}
                aria-hidden="true"
              />
              <span
                style={{
                  fontSize: 11,
                  lineHeight: '16px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '.12em',
                  color: DARK_INK.secondary.hex,
                  whiteSpace: 'nowrap',
                }}
              >
                {label ? t(label) : null}
              </span>
            </span>
          </div>
        </>
      ) : (
        /* Chip dropped (brief §2.2 "does it earn its place?") — a single
           quiet ≤24px tick through the seam, not a full-height trace. */
        <div
          aria-hidden="true"
          className="narrative-leader absolute left-1/2 top-1/2 h-6 w-px -translate-x-1/2 -translate-y-1/2"
        />
      )}
    </div>
  );
}

export interface DisclaimerDividerProps {
  fromBand: LandingBand;
  toBand: LandingBand;
  /** Stage kicker (F owns render, E authors the string). */
  kicker: BilingualContent;
  /** Trial/card terms bubble copy (E authors; renders via E's MarketingDisclaimer). */
  trialCopy: MarketingDisclaimerCopy;
}

/**
 * DisclaimerDivider — boundary #4 (Pricing → Bot). Judge F owns the interleaved
 * slot, seam, dotted leaders and stage kicker; Judge E owns the bubble anatomy +
 * binding trial/card copy (rulings Harm. b). A commercial disclaimer is never
 * pinned, drawn, or passed under momentum, so this slot is fully static.
 *
 * Deviation (noted): E's MarketingDisclaimer is composed with placement
 * `floating` (bare bubble) and centered by THIS slot's own wrapper, rather than
 * placement `seam`. E's `seam` transform (translateY(50%), no top anchor) sits
 * the bubble on a container's flow edge, which cannot deterministically center
 * on this slot's geometric MIDPOINT seam across the 44px/68px bubble heights.
 * `floating` + F-owned centering yields the exact Harm. b spec: exactly one
 * #FFFFFF12 line, owned by the divider, with the bubble centered on it.
 *
 * 2026-07-18 slop-audit revision pass FIX 2: this component has exactly ONE
 * real caller (`LandingPage.tsx`'s Pricing→Bot boundary), which is ALWAYS a
 * paper-to-paper transition post-R7 — unlike `NarrativeHandoff` above, this
 * component never branched on surface at all, so its kicker text, leader
 * dashes, and midpoint seam were still hardcoded to the pre-R7 dark
 * palette (`DARK_INK.tertiary`, `--connector-stroke`'s white-alpha
 * fallback, `--connector-seam`'s white-alpha fallback) — all three
 * effectively invisible against paper. Re-inked directly (no new `surface`
 * prop needed — this component genuinely has only one, always-paper,
 * caller): kicker → `PAPER_INK.muted`; midpoint seam → the EXISTING
 * `--connector-seam-light` variable (`NarrativeHandoff`'s own light branch
 * already established this token, reused here rather than inventing a
 * second one); leaders → a local `--connector-stroke` override on this
 * root (CSS custom properties inherit to the `.narrative-leader` children
 * below without touching the shared global rule or `NarrativeHandoff`'s
 * still-genuinely-dark leader usage).
 */
export function DisclaimerDivider({ fromBand, toBand, kicker, trialCopy }: DisclaimerDividerProps) {
  const { t } = useLocale();

  return (
    <div
      data-narrative-divider
      className="landing-motion-scope relative w-full h-[64px] lg:h-[96px]"
      style={{ '--connector-stroke': 'rgba(0, 0, 0, 0.18)' } as CSSProperties}
    >
      {/* Solid half-bands + ONE midpoint seam. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-1/2"
        style={{ background: fromBand }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-1/2"
        style={{ background: toBand }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2"
        style={{ background: 'var(--connector-seam-light, rgba(0, 0, 0, 0.08))' }}
      />

      {/* 24px dotted leaders above + below the bubble (static; behind the tile). */}
      <div
        aria-hidden="true"
        className="narrative-leader absolute left-1/2 top-0 h-6 w-px -translate-x-1/2"
      />
      <div
        aria-hidden="true"
        className="narrative-leader absolute bottom-0 left-1/2 h-6 w-px -translate-x-1/2"
      />

      {/* Kicker eyebrow + E's bubble, centered on the seam as one boundary marker. */}
      <div className="absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2">
        <span
          className="whitespace-nowrap"
          style={{
            fontSize: 10,
            lineHeight: '14px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '.12em',
            color: PAPER_INK.muted.hex,
          }}
        >
          {t(kicker)}
        </span>
        <MarketingDisclaimer copy={trialCopy} icon="card" placement="floating" />
      </div>
    </div>
  );
}
